import { useCallback, useEffect, useRef, useState } from "react";
import IntegrationApi from "../../apis/integrationApi";
import type { AutoAgentMonitorOwnerSection, AutoAgentMonitorTicket } from "../../types/integration";
import type { AutoAgentMonitorFilterQuery } from "../theoAutomationsMonitorFilters";
import { EMPTY_OWNER_COUNTS, livePollDelayMs, OWNER_GROUPS, OwnerCounts } from "./executionQueueModel";

const integrationApi = new IntegrationApi();

export interface QueueGroupState {
  page: number;
  rows: AutoAgentMonitorTicket[];
  totalCount: number;
  loading: boolean;
  error: string | null;
}

type GroupMap<T> = Record<AutoAgentMonitorOwnerSection, T>;

function groupMap<T>(make: (key: AutoAgentMonitorOwnerSection) => T): GroupMap<T> {
  return Object.fromEntries(OWNER_GROUPS.map((group) => [group.key, make(group.key)])) as GroupMap<T>;
}

const initialGroup = (): QueueGroupState => ({ page: 0, rows: [], totalCount: 0, loading: false, error: null });

interface LoadOptions {
  page?: number;
  refreshStatuses?: boolean;
  background?: boolean;
  generation?: number;
}

interface UseExecutionQueueArgs {
  agentId: number;
  filterQuery: AutoAgentMonitorFilterQuery;
  /** Changes whenever the filters do; restarts the queue from page one. */
  filterKey: string;
  /** Bumped by the page's Refresh button: reload everything and re-read PSA statuses. */
  refreshToken: number;
}

/**
 * Loads the four owner groups and keeps them live. Polls every few seconds while Theo has a run in
 * flight and slowly otherwise, skips polls while the tab is hidden, and never re-reads PSA statuses
 * on a poll (that is a PSA API call per stale row).
 */
export default function useExecutionQueue({ agentId, filterQuery, filterKey, refreshToken }: UseExecutionQueueArgs) {
  const [groups, setGroups] = useState<GroupMap<QueueGroupState>>(() => groupMap(initialGroup));
  const [expanded, setExpanded] = useState<GroupMap<boolean>>(() =>
    groupMap((key) => !OWNER_GROUPS.find((group) => group.key === key)!.defaultCollapsed)
  );
  const [counts, setCounts] = useState<OwnerCounts>(EMPTY_OWNER_COUNTS);
  const [refreshedAt, setRefreshedAt] = useState<string | null>(null);
  const [liveError, setLiveError] = useState<string | null>(null);

  const groupsRef = useRef(groups);
  groupsRef.current = groups;
  const expandedRef = useRef(expanded);
  expandedRef.current = expanded;
  const countsRef = useRef(counts);
  countsRef.current = counts;
  const filterQueryRef = useRef(filterQuery);
  filterQueryRef.current = filterQuery;
  const generationRef = useRef(0);

  const loadGroup = useCallback(
    async (section: AutoAgentMonitorOwnerSection, options: LoadOptions = {}) => {
      const config = OWNER_GROUPS.find((group) => group.key === section)!;
      const generation = options.generation ?? generationRef.current;
      const page = options.page ?? groupsRef.current[section].page;
      if (!options.background) {
        setGroups((prev) => ({ ...prev, [section]: { ...prev[section], page, loading: true, error: null } }));
      }
      try {
        const response = await integrationApi.getAutoAgentMonitor({
          section,
          page: page + 1,
          pageSize: config.pageSize,
          refreshStatuses: options.refreshStatuses ?? false,
          sortBy: "last_activity",
          sortDirection: config.sortDirection,
          agentId,
          ...filterQueryRef.current,
        });
        if (generation !== generationRef.current) return;
        if (response.summary?.section_counts) setCounts(response.summary.section_counts);
        setRefreshedAt(response.summary?.refreshed_at || null);
        setLiveError(null);
        setGroups((prev) => ({
          ...prev,
          [section]: { page, rows: response.rows || [], totalCount: response.totalCount || 0, loading: false, error: null },
        }));
      } catch (error: unknown) {
        if (generation !== generationRef.current) return;
        const message = error instanceof Error ? error.message : "Couldn't load auto-agent tickets.";
        if (options.background) {
          setLiveError(message);
          return;
        }
        setGroups((prev) => ({ ...prev, [section]: { ...prev[section], loading: false, error: message } }));
      }
    },
    [agentId]
  );

  /** Collapsed groups only need their count, which every response carries. */
  const loadVisible = useCallback(
    (options: LoadOptions = {}) =>
      Promise.all(
        OWNER_GROUPS.filter((group) => expandedRef.current[group.key]).map((group) => loadGroup(group.key, options))
      ),
    [loadGroup]
  );

  useEffect(() => {
    const generation = generationRef.current + 1;
    generationRef.current = generation;
    groupsRef.current = groupMap((key) => ({ ...groupsRef.current[key], page: 0 }));
    setGroups(groupsRef.current);
    let cancelled = false;
    void (async () => {
      await loadVisible({ page: 0, generation });
      if (cancelled || generation !== generationRef.current) return;
      // PSA statuses go stale between visits; re-read them once, quietly, after the fast first paint.
      await loadVisible({ refreshStatuses: true, background: true, generation });
    })();
    return () => {
      cancelled = true;
    };
  }, [filterKey, loadVisible]);

  const didMountRefresh = useRef(false);
  useEffect(() => {
    if (!didMountRefresh.current) {
      didMountRefresh.current = true;
      return;
    }
    void loadVisible({ refreshStatuses: true });
  }, [refreshToken, loadVisible]);

  const isActive = counts.working > 0;
  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const schedule = () => {
      if (!cancelled) timer = setTimeout(tick, livePollDelayMs(countsRef.current));
    };
    async function tick() {
      if (!document.hidden) await loadVisible({ background: true });
      schedule();
    }
    const onVisibilityChange = () => {
      if (document.hidden || cancelled) return;
      if (timer) clearTimeout(timer);
      void tick();
    };
    schedule();
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [isActive, filterKey, loadVisible]);

  const toggleGroup = useCallback(
    (section: AutoAgentMonitorOwnerSection) => {
      const next = !expandedRef.current[section];
      expandedRef.current = { ...expandedRef.current, [section]: next };
      setExpanded(expandedRef.current);
      if (next) void loadGroup(section);
    },
    [loadGroup]
  );

  const setPage = useCallback(
    (section: AutoAgentMonitorOwnerSection, page: number) => {
      void loadGroup(section, { page });
    },
    [loadGroup]
  );

  return { groups, counts, expanded, refreshedAt, liveError, toggleGroup, setPage };
}
